import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import { validateAdminPassword } from '../../utils/passwordPolicy';
import { INITIAL_FORM } from './constants';

const validateStep1 = (form) => {
  const errs = {};
  if (!form.agency_name.trim()) errs.agency_name = 'Required';
  if (!/^\d{10}$/.test(form.npi)) errs.npi = 'Must be 10 digits';
  if (!form.tax_id.trim()) {
    errs.tax_id = 'Required';
  } else {
    // P52 — Tax ID (EIN) must be 9 digits after stripping the optional dash.
    // Examples accepted: "12-3456789", "123456789".
    const digits = form.tax_id.replace(/\D/g, '');
    if (digits.length !== 9) errs.tax_id = 'Must be 9 digits (EIN format: XX-XXXXXXX)';
  }
  return errs;
};

const validateStep3 = (form) => {
  const errs = {};
  if (!form.street1.trim()) errs.street1 = 'Required';
  if (!form.city.trim()) errs.city = 'Required';
  if (!form.zip_code.trim()) errs.zip_code = 'Required';
  if (!form.contact_name.trim()) errs.contact_name = 'Required';
  if (!form.contact_phone.trim()) errs.contact_phone = 'Required';
  if (!form.contact_email.trim()) errs.contact_email = 'Required';
  return errs;
};

const validateStep4 = (form) => {
  const errs = {};
  if (!form.admin_name.trim()) errs.admin_name = 'Required';
  if (!form.admin_email.trim()) errs.admin_email = 'Required';
  else if (!/\S+@\S+\.\S+/.test(form.admin_email)) errs.admin_email = 'Invalid email';
  const passwordError = validateAdminPassword(form.admin_password);
  if (passwordError) errs.admin_password = passwordError;
  if (form.admin_password !== form.admin_password_confirm)
    errs.admin_password_confirm = "Passwords don't match";
  return errs;
};

const VALIDATORS = {
  1: validateStep1,
  3: validateStep3,
  4: validateStep4,
};

const buildSignupPayload = (form) => {
  const taxonomy =
    form.taxonomy_code === 'other' ? form.taxonomy_other : form.taxonomy_code;
  const hasSandata =
    form.sandata_provider_id || form.sandata_provider_code || form.sandata_medicaid_id;
  return {
    agency_name: form.agency_name.trim(),
    dba_name: form.dba_name.trim() || null,
    npi: form.npi.trim(),
    tax_id: form.tax_id.trim(),
    medicaid_provider_id: form.medicaid_provider_id.trim() || null,
    taxonomy_code: taxonomy || null,
    sandata: hasSandata
      ? {
          provider_id: form.sandata_provider_id.trim() || null,
          provider_code: form.sandata_provider_code.trim() || null,
          medicaid_id: form.sandata_medicaid_id.trim() || null,
          api_username: form.sandata_api_username.trim() || null,
          api_password: form.sandata_api_password.trim() || null,
        }
      : null,
    address: {
      street1: form.street1.trim(),
      street2: form.street2.trim() || null,
      city: form.city.trim(),
      state: form.state,
      zip_code: form.zip_code.trim(),
      county: form.county.trim() || null,
    },
    contact: {
      name: form.contact_name.trim(),
      title: form.contact_title.trim() || null,
      phone: form.contact_phone.trim(),
      email: form.contact_email.trim(),
      fax: form.contact_fax.trim() || null,
    },
    admin_name: form.admin_name.trim(),
    admin_email: form.admin_email.trim(),
    admin_password: form.admin_password,
  };
};

export const useAgencySignup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(INITIAL_FORM);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateStep = (s) => {
    const validator = VALIDATORS[s];
    const errs = validator ? validator(form) : {};
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, 5));
  };
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      setStep(4);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/agency-signup', buildSignupPayload(form));
      toast.success('Agency registered! Your account is pending approval.');
      navigate('/login');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Signup failed';
      toast.error(detail);
      if (detail.includes('NPI')) {
        setStep(1);
        setErrors({ npi: detail });
      } else if (detail.includes('Email') || detail.includes('email')) {
        setStep(4);
        setErrors({ admin_email: detail });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    step,
    setStep,
    form,
    errors,
    submitting,
    set,
    setField,
    nextStep,
    prevStep,
    handleSubmit,
  };
};
